import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { BriefingStageRecord } from "@/lib/briefingStages";

/**
 * Persist a new stage order for one briefing + day via the reorder_briefing_stages
 * RPC, with an optimistic cache update + rollback. Shared by the schedule creator
 * and the read-only Schedule page so the drag-reorder persistence stays identical
 * in both. Pair the returned mutation with {@link useStageReorder} for the DnD UI.
 */
export function useReorderStages(briefingId: string, eventDate: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const { error } = await supabase.rpc("reorder_briefing_stages", {
        p_briefing_id: briefingId,
        p_event_date: eventDate,
        p_ordered_ids: orderedIds,
      });
      if (error) throw error;
    },
    onMutate: async (orderedIds: string[]) => {
      await queryClient.cancelQueries({ queryKey: ["briefing_stages", briefingId] });
      const prev = queryClient.getQueryData<BriefingStageRecord[]>(["briefing_stages", briefingId]);
      if (prev) {
        const orderIndex = new Map(orderedIds.map((id, i) => [id, i]));
        queryClient.setQueryData<BriefingStageRecord[]>(
          ["briefing_stages", briefingId],
          prev.map((s) =>
            s.event_date === eventDate && orderIndex.has(s.id)
              ? { ...s, display_order: orderIndex.get(s.id)! }
              : s,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["briefing_stages", briefingId], ctx.prev);
      toast.error("Couldn't save the stage order");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["briefing_stages", briefingId] });
    },
  });
}
